import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';

// --- GraphQL Query ---
const GET_ALL_GROUPS = gql`
    query GetAllGroups {
        allGroups {
            __typename
            id
            name
            ... on Group {
                students { id }
            }
        }
    }
`;

function NoGroupsFound() {
    return (
        <div className="text-center py-16">
            <h3 className="text-lg font-medium text-gray-800">Keine Gruppen gefunden</h3>
            <p className="mt-1 text-sm text-gray-500">
                Es gibt keine Studentengruppen. Erstelle eine neue Gruppe, um zu beginnen.
            </p>
            <div className="mt-6">
                <Link to="/admin/groups/create" className="mainBtn inline-flex items-center text-white px-6 py-2">
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Gruppe erstellen
                </Link>
            </div>
        </div>
    );
}

export default function GroupsManage() {
    const { loading, error, data } = useQuery(GET_ALL_GROUPS, {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
    });

    if (loading) {
        return <div className="py-8 text-center">Loading groups...</div>;
    }

    if (error) {
        return <div className="py-8 text-center text-red-600">Error loading groups: {error.message}</div>;
    }

    const groups = data?.allGroups || [];

    if (!groups || groups.length === 0) {
        return <NoGroupsFound />;
    }

    return (
        <div className="w-full">
            <header className="mb-6 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <h1 className="text-3xl font-bold text-neutral-900">Gruppenübersicht</h1>
                <Link to="/admin/groups/create" className="mainBtn inline-flex items-center justify-center sm:w-auto w-full text-white px-5 py-2">
                    <svg className="mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    <span>Gruppe erstellen</span>
                </Link>
            </header>

            <main>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-6">
                    {groups.filter(g => g.__typename !== 'SchoolClass').map((group) => {
                        const isGroupType = group.__typename === 'Group';
                        const studentCount = isGroupType ? (group.students ? group.students.length : 0) : null;
                        return (
                            <Link
                                to={`/admin/groups/${group.id}`}
                                key={group.id}
                                className="group wrap block bg-white p-6 rounded-xl border-2 border-neutral-200 transition-all duration-100 hover:border-neutral-300"
                            >
                                <div className="flex justify-between items-start">
                                    <h2 className="text-xl font-bold text-neutral-800 truncate" title={group.name}>
                                        {group.name}
                                    </h2>
                                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${isGroupType ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'}`}>
                                        {isGroupType ? 'Gruppe' : 'Klasse'}
                                    </span>
                                </div>

                                <div className="mt-4 pt-4 border-t-2 border-neutral-100 space-y-3">
                                    {isGroupType ? (
                                        <p className="flex items-center text-neutral-600">
                                            <svg className="h-5 w-5 mr-3 text-neutral-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <span>{studentCount} Schüler</span>
                                        </p>
                                    ) : (
                                        <p className="text-sm text-neutral-500">(keine Studentendaten verfügbar)</p>
                                    )}
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </main>
        </div>
    );
}
